import React, { useState } from 'react';
import { Tag, Plus, Pencil, Trash2, Check, X, Palette, Layers, Pipette, ChevronDown } from 'lucide-react';
import { useServiceCategories, useServices, useLookupMutations } from '../../db/hooks/useLookupData';
import { Drawer } from '../common/Drawer';
import { Button } from '../common/Button';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useToast } from '../common/ToastProvider';
import type { ServiceCategoryDocType } from '../../db/schemas/service-category.schema';

interface CategoryManagerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PRESET_CATEGORY_COLORS = [
  '#F97316', // Orange
  '#06B6D4', // Cyan
  '#8B5CF6', // Purple
  '#10B981', // Emerald
  '#EC4899', // Pink
  '#EAB308', // Yellow
  '#3B82F6', // Blue
];

export function CategoryManagerDrawer({ isOpen, onClose }: CategoryManagerDrawerProps) {
  const { serviceCategories } = useServiceCategories();
  const { services } = useServices();
  const { addServiceCategory, updateServiceCategory, deleteServiceCategory } = useLookupMutations();
  const toast = useToast();

  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState(PRESET_CATEGORY_COLORS[0]);
  const [isCreating, setIsCreating] = useState(false);

  const [editingCatId, setEditingCatId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingColor, setEditingColor] = useState('');

  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    cat: ServiceCategoryDocType | null;
  }>({
    isOpen: false,
    cat: null,
  });

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCatName.trim()) {
      toast.error('El nombre de la categoría es obligatorio.', 'Campo Requerido');
      return;
    }

    try {
      setIsCreating(true);
      await addServiceCategory({
        name: newCatName.trim(),
        color: newCatColor,
        sort_order: serviceCategories.length,
      });
      toast.success(`Categoría "${newCatName.trim()}" creada con éxito.`);
      setNewCatName('');
      setNewCatColor(PRESET_CATEGORY_COLORS[0]);
      setIsFormExpanded(false);
    } catch (err: any) {
      toast.error('Error al crear categoría: ' + (err?.message || 'Error desconocido'));
    } finally {
      setIsCreating(false);
    }
  };

  const startEditing = (cat: ServiceCategoryDocType) => {
    setEditingCatId(cat.id);
    setEditingName(cat.name);
    setEditingColor(cat.color || PRESET_CATEGORY_COLORS[0]);
  };

  const cancelEditing = () => {
    setEditingCatId(null);
    setEditingName('');
    setEditingColor('');
  };

  const handleSaveEdit = async (id: string) => {
    if (!editingName.trim()) {
      toast.error('El nombre de la categoría no puede estar vacío.');
      return;
    }

    try {
      await updateServiceCategory(id, {
        name: editingName.trim(),
        color: editingColor,
      });
      toast.success('Categoría actualizada correctamente.');
      cancelEditing();
    } catch (err: any) {
      toast.error('Error al actualizar categoría: ' + (err?.message || 'Error desconocido'));
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteConfirmModal.cat) return;
    const cat = deleteConfirmModal.cat;

    try {
      await deleteServiceCategory(cat.id);
      toast.success(`Categoría "${cat.name}" eliminada.`);
    } catch (err: any) {
      toast.error('Error al eliminar categoría: ' + (err?.message || 'Error desconocido'));
    } finally {
      setDeleteConfirmModal({ isOpen: false, cat: null });
    }
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title="Gestionar Categorías"
        subtitle="Organiza, edita y personaliza categorías para clasificar tus servicios"
        icon={Tag}
        maxWidth="md"
        footer={
          <div className="flex justify-end w-full">
            <Button variant="primary" size="sm" onClick={onClose}>
              Listo
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Collapsible New Category Form */}
          {!isFormExpanded ? (
            <button
              type="button"
              onClick={() => setIsFormExpanded(true)}
              className="w-full py-3 px-4 bg-surface-secondary/60 hover:bg-surface-secondary border border-dashed border-border hover:border-brand-orange/40 rounded-2xl flex items-center justify-center gap-2 text-xs font-bold text-brand-orange transition-all cursor-pointer shadow-xs"
            >
              <Plus className="w-4 h-4" />
              <span>Nueva Categoría de Servicio</span>
            </button>
          ) : (
            <form
              onSubmit={handleCreateCategory}
              className="p-4 bg-surface-secondary/80 border border-border rounded-2xl space-y-4 shadow-sm animate-fade-in"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-orange">
                  <Plus className="w-4 h-4" />
                  <span>Nueva Categoría</span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsFormExpanded(false)}
                  className="text-text-tertiary hover:text-text-primary p-1 rounded-lg transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-text-secondary mb-1">
                  Nombre de la Categoría *
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  placeholder="Ej: Depilación Láser, Tratamientos Faciales, Odontología..."
                  className="w-full h-10 rounded-xl border border-border bg-surface px-3 text-sm text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all shadow-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-text-secondary mb-1.5 flex items-center gap-1.5">
                  <Palette className="w-3.5 h-3.5 text-text-tertiary" />
                  <span>Color distintivo</span>
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  {PRESET_CATEGORY_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewCatColor(c)}
                      className={`w-7 h-7 rounded-full transition-all cursor-pointer flex items-center justify-center border-2 ${
                        newCatColor === c ? 'border-white scale-110 shadow-sm' : 'border-transparent opacity-80 hover:opacity-100'
                      }`}
                      style={{ backgroundColor: c }}
                    >
                      {newCatColor === c && <Check className="w-3.5 h-3.5 text-white drop-shadow-sm" />}
                    </button>
                  ))}

                  {/* Custom Color Picker Button */}
                  <label
                    className={`relative w-7 h-7 rounded-full transition-all cursor-pointer flex items-center justify-center border-2 overflow-hidden ${
                      !PRESET_CATEGORY_COLORS.includes(newCatColor)
                        ? 'border-white scale-110 shadow-md ring-2 ring-brand-sky/50'
                        : 'border-border/60 opacity-80 hover:opacity-100'
                    }`}
                    style={{
                      background: !PRESET_CATEGORY_COLORS.includes(newCatColor)
                        ? newCatColor
                        : 'conic-gradient(from 180deg at 50% 50%, #f43f5e 0deg, #eab308 60deg, #10b981 120deg, #06b6d4 180deg, #8b5cf6 240deg, #ec4899 300deg, #f43f5e 360deg)',
                    }}
                    title="Elegir color personalizado"
                  >
                    <input
                      type="color"
                      value={newCatColor}
                      onChange={(e) => setNewCatColor(e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                    <Pipette className="w-3.5 h-3.5 text-white drop-shadow-md pointer-events-none" />
                  </label>
                </div>
              </div>

              <div className="pt-1 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsFormExpanded(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  icon={Plus}
                  disabled={isCreating || !newCatName.trim()}
                >
                  {isCreating ? 'Guardando...' : 'Crear Categoría'}
                </Button>
              </div>
            </form>
          )}

          {/* List of existing categories */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <span className="text-xs font-bold uppercase tracking-wider text-text-tertiary flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5" />
                <span>Categorías Existentes ({serviceCategories.length})</span>
              </span>
            </div>

            {serviceCategories.length === 0 ? (
              <div className="p-8 text-center bg-surface-secondary/40 border border-dashed border-border rounded-2xl text-text-tertiary text-sm">
                No hay categorías registradas. Puedes crear una arriba o dejar tus servicios sin categoría.
              </div>
            ) : (
              <div className="space-y-2">
                {serviceCategories.map((cat) => {
                  const isEditing = editingCatId === cat.id;
                  const serviceCount = services.filter((s) => s.category_id === cat.id).length;
                  const catColor = cat.color || PRESET_CATEGORY_COLORS[0];

                  if (isEditing) {
                    return (
                      <div
                        key={cat.id}
                        className="p-3.5 bg-surface border border-brand-sky/50 rounded-xl space-y-3 shadow-md animate-fade-in"
                      >
                        <div>
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="w-full h-9 rounded-lg border border-border bg-surface-secondary px-3 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky"
                            placeholder="Nombre de la categoría..."
                            autoFocus
                          />
                        </div>

                        <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                          <div className="flex items-center gap-1.5">
                            {PRESET_CATEGORY_COLORS.map((c) => (
                              <button
                                key={c}
                                type="button"
                                onClick={() => setEditingColor(c)}
                                className={`w-5 h-5 rounded-full transition-all cursor-pointer flex items-center justify-center border ${
                                  editingColor === c ? 'border-white scale-110 shadow-xs' : 'border-transparent opacity-70'
                                }`}
                                style={{ backgroundColor: c }}
                              >
                                {editingColor === c && <Check className="w-2.5 h-2.5 text-white" />}
                              </button>
                            ))}

                            {/* Custom Color in Edit mode */}
                            <label
                              className={`relative w-5 h-5 rounded-full transition-all cursor-pointer flex items-center justify-center border overflow-hidden ${
                                !PRESET_CATEGORY_COLORS.includes(editingColor)
                                  ? 'border-white scale-110 shadow-xs ring-1 ring-brand-sky'
                                  : 'border-border opacity-70 hover:opacity-100'
                              }`}
                              style={{
                                background: !PRESET_CATEGORY_COLORS.includes(editingColor)
                                  ? editingColor
                                  : 'conic-gradient(from 180deg at 50% 50%, #f43f5e 0deg, #eab308 60deg, #10b981 120deg, #06b6d4 180deg, #8b5cf6 240deg, #ec4899 300deg, #f43f5e 360deg)',
                              }}
                              title="Color personalizado"
                            >
                              <input
                                type="color"
                                value={editingColor}
                                onChange={(e) => setEditingColor(e.target.value)}
                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                              />
                            </label>
                          </div>

                          <div className="flex items-center gap-1.5 ml-auto">
                            <Button variant="ghost" size="sm" icon={X} onClick={cancelEditing}>
                              Cancelar
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              icon={Check}
                              onClick={() => handleSaveEdit(cat.id)}
                            >
                              Guardar
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={cat.id}
                      className="flex items-center justify-between p-3 bg-surface border border-border/70 rounded-xl hover:border-border transition-all group shadow-xs"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="w-3.5 h-3.5 rounded-full shrink-0 shadow-xs"
                          style={{ backgroundColor: catColor }}
                        />
                        <div className="truncate">
                          <p className="text-sm font-bold text-text-primary truncate">{cat.name}</p>
                          <p className="text-xs text-text-tertiary">
                            {serviceCount} {serviceCount === 1 ? 'servicio' : 'servicios'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => startEditing(cat)}
                          className="p-1.5 text-text-tertiary hover:text-text-primary hover:bg-surface-secondary rounded-lg transition-colors cursor-pointer"
                          title="Editar categoría"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmModal({ isOpen: true, cat })}
                          className="p-1.5 text-text-tertiary hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors cursor-pointer"
                          title="Eliminar categoría"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Drawer>

      <ConfirmDialog
        isOpen={deleteConfirmModal.isOpen}
        onClose={() => setDeleteConfirmModal({ isOpen: false, cat: null })}
        onConfirm={handleDeleteCategory}
        title="Eliminar Categoría"
        message={`¿Estás seguro de que deseas eliminar la categoría "${deleteConfirmModal.cat?.name}"? Los servicios existentes asociados pasarán a "Sin categoría".`}
        variant="danger"
        confirmText="Eliminar Categoría"
      />
    </>
  );
}
